import {
    createBrowserRouter,
    RouterProvider,
    Navigate,
    useLocation,
} from 'react-router-dom'
import ReactGA from 'react-ga4'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Home from './pages/home'
import UserConfigs from './pages/userConfigs'
import Login from './pages/login'
import AuthProvider from './contexts/auth'
import {CookiesService} from './services/Cookie.service'
import ValidateEmailPage from './pages/validateEmail'
import ValidateSubscriptionPage from './pages/validateSubscription'
import GeneratorWaitingPage from './pages/GeradorDocumentos/generator-waiting-page'
import DocumentGenerator from './pages/GeradorDocumentos/document-generator'
import ProjetosPage from './pages/GeradorDocumentos/projetos'
import FlowPage from './pages/FlowQuestion'
import ValidateInvitePage from './pages/validateInvite'
import ErrorPage from './pages/error'
import {Outlet} from 'react-router-dom'
import HistoryProvider from './contexts/history'
import RED from './pages/R.E.D'
import CaronaInteligente from './pages/CaronaInteligente'
import ResetPassword from './pages/login/components/Form/resetPassword'
import SuportForm from './pages/suport'
import UploadDocuments from './pages/uploadDocuments'
import PesquisaPrecos from './pages/PesquisaPrecos'
import LevantamentoMercado from './pages/LevantamentoMercado'
import ContratacaoDireta from './pages/ContratacaoDireta'
import RadarFornecedor from './pages/RadarFornecedor'
import TrilhaGuru from './pages/TrilhaGuru'
import TrilhaGuruAdmin from './pages/TrilhaGuru/Admin'
import AdminMenu from './pages/admin'
import FlowOrganizationsPage from './pages/flowOrganizations'
import BasesDeDados from './pages/BasesDeDados'
import BasesDeDadosDetalhe from './pages/BasesDeDados/Detalhe'
import BasesDeDadosNovo from './pages/BasesDeDados/Novo'
import AuditoriaPage from './pages/Auditoria'

function validateToken() {
    const cookiesService = new CookiesService()
    const authData = cookiesService.getAuthData()
    const isAuthenticated = authData?.access_token
    return isAuthenticated
}

// Remonta o componente sempre que o menu lateral navegar para a mesma rota,
// devolvendo o módulo à sua tela inicial mesmo em meio a um fluxo.
const RemountOnNavigate = ({children}: {children: JSX.Element}) => {
    const location = useLocation()
    return <div key={location.key}>{children}</div>
}

// Slug antigo do Ata Inteligente ("carona") → slug atual, preservando query
// string e state: links salvos e handoffs de builds antigos (o Levantamento
// abria /carona?origem=levantamento&...) continuam entrando no módulo.
const LegacyCaronaRedirect = () => {
    const location = useLocation()
    // Pageview do slug antigo: mede quanto tráfego ainda chega por /carona —
    // é o dado que diz quando este redirect pode ser aposentado.
    ReactGA.send({
        hitType: 'pageview',
        page: '/carona',
        title: 'Ata Inteligente (slug antigo)',
    })
    return (
        <Navigate
            to={{
                pathname: '/ata-inteligente',
                search: location.search,
                hash: location.hash,
            }}
            state={location.state}
            replace
        />
    )
}

const PrivateRoutesLayout = () => {
    return (
        <HistoryProvider>
            <ToastContainer
                position="top-right"
                autoClose={10000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <Outlet />
        </HistoryProvider>
    )
}

const PublicRoute = (path: string, element: JSX.Element) => {
    const isAuthenticated = validateToken() && path.includes('login')
    return isAuthenticated
        ? {path, element: <Navigate to={'/'} />}
        : {path, element}
}
const ErrorRoute = (path: string, element: JSX.Element) => {
    return {path, element}
}
const router = createBrowserRouter([
    PublicRoute('/login', <Login />),
    PublicRoute('/admin', <Login />),
    PublicRoute('/login/confirm_email/:token', <ValidateEmailPage />),
    PublicRoute('/login/reset_password', <ResetPassword />),
    PublicRoute('/v1/login/first_login/:token', <ResetPassword />),
    PublicRoute('/login/invite/accept/:token', <ValidateInvitePage />),
    PublicRoute('/subscriptions/handle', <ValidateSubscriptionPage />),

    {
        element: validateToken() ? (
            <PrivateRoutesLayout />
        ) : (
            <Navigate to="/login" />
        ),

        children: [
            {path: '/', element: <Home />},
            {path: '/gerador', element: <DocumentGenerator />},
            {path: '/espera', element: <GeneratorWaitingPage />},
            {path: '/projetos', element: <ProjetosPage />},
            {path: '/configuracoes', element: <UserConfigs />},
            {path: '/flowPage', element: <FlowPage />},
            {path: '/adminPage', element: <AdminMenu />},
            {path: '/flowOrganizationsPage', element: <FlowOrganizationsPage />},
            {path: '/analisador', element: <RED />},
            {path: '/bases-de-dados', element: <BasesDeDados />},
            {path: '/bases-de-dados/novo', element: <BasesDeDadosNovo />},
            {
                path: '/bases-de-dados/:nome',
                element: <BasesDeDadosDetalhe />,
            },
            {
                path: '/ata-inteligente',
                element: (
                    <RemountOnNavigate>
                        <CaronaInteligente />
                    </RemountOnNavigate>
                ),
            },
            {path: '/carona', element: <LegacyCaronaRedirect />},
            {path: '/suporte', element: <SuportForm />},
            {path: 'upload-documents', element: <UploadDocuments />},
            {
                path: '/pesquisa-precos',
                element: (
                    <RemountOnNavigate>
                        <PesquisaPrecos />
                    </RemountOnNavigate>
                ),
            },
            {
                path: '/levantamento-mercado',
                element: (
                    <RemountOnNavigate>
                        <LevantamentoMercado />
                    </RemountOnNavigate>
                ),
            },
            {path: '/contratacao-direta', element: <ContratacaoDireta />},
            {
                path: '/radar-fornecedor',
                element: (
                    <RemountOnNavigate>
                        <RadarFornecedor />
                    </RemountOnNavigate>
                ),
            },
            // SEM RemountOnNavigate, de propósito: clicar no menu em meio a
            // uma lição remontaria a página e jogaria fora a sessão de estudo
            // (pílulas respondidas, fila, fase) — que vive em state, não em
            // rota. O custo aceito é não voltar à tela inicial nem re-buscar o
            // progresso ao re-clicar o item do menu já dentro do módulo.
            {path: '/trilha-guru', element: <TrilhaGuru />},
            {path: '/trilha-guru/admin', element: <TrilhaGuruAdmin />},
            {path: '/admin/auditoria', element: <AuditoriaPage />},
        ],
    },

    ErrorRoute('*', <ErrorPage />),
])

export default function AppRoutes() {
    return (
        <AuthProvider>
            <RouterProvider router={router} />
        </AuthProvider>
    )
}
